/*!
  tooltips.js
  v1.0  •  MIT License
  Refactored to use <details>/<summary> and a single CSS block.
*/
(function () {
  'use strict';

  /**
   * 1) Extracts the first [[info:…]] token from a string.
   *    Returns { cleanText, infoText } or null if no token is present.
   */
  function extractInfoToken(fullString) {
    const regex = /\[\[info:(.*?)\]\]/;
    const match = fullString.match(regex);
    if (!match) return null;
    const infoText = match[1].trim();
    const cleanText = fullString.replace(regex, '').trim();
    return { cleanText, infoText };
  }

  /**
   * 2) Creates a <details class="ef-tooltip-container">…</details> block
   *    with a <summary>?</summary> and a <div class="ef-tooltip-text">…</div>.
   *    Also wires up a toggle listener to close other tooltips when one opens.
   */
  function createTooltipContainer(infoText) {
    // (2a) <details> wrapper
    const details = document.createElement('details');
    details.className = 'ef-tooltip-container';

    // (2b) <summary> as the “?” icon
    const summary = document.createElement('summary');
    summary.textContent = '?';
    summary.setAttribute('aria-expanded', 'false');
    summary.setAttribute('role', 'button');
    details.appendChild(summary);

    // (2c) <div class="ef-tooltip-text">…</div>
    const bubble = document.createElement('div');
    bubble.className = 'ef-tooltip-text';
    bubble.setAttribute('role', 'tooltip');
    bubble.textContent = infoText;
    details.appendChild(bubble);

    // (2d) Whenever this <details> is toggled open, close all other open tooltips:
    details.addEventListener('toggle', () => {
      const isOpen = details.hasAttribute('open');
      // Update aria-expanded on summary
      summary.setAttribute('aria-expanded', isOpen);
      if (isOpen) {
        // Close any other tooltip that’s open
        document
          .querySelectorAll('.ef-tooltip-container[open]')
          .forEach((other) => {
            if (other !== details) {
              other.removeAttribute('open');
            }
          });
      }
    });

    return details;
  }

  /**
   * 3) Given an element `el`, either:
   *    • If `isAttribute === true`, we look at el.getAttribute(attributeName),
   *      strip out [[info:…]] from that attribute, update the attribute to the
   *      clean text, and then inject a tooltip into the visible title DIV.
   *    • Otherwise, we look inside el.innerHTML for [[info:…]] and do the same.
   */
  function replaceInfoTokensInElement(el, isAttribute, attributeName) {
    if (isAttribute) {
      const rawValue = el.getAttribute(attributeName);
      if (!rawValue) return;
      const extracted = extractInfoToken(rawValue);
      if (!extracted) return;

      const { cleanText, infoText } = extracted;
      // Find the visible title container (e.g. .ef__option-title) if it exists
      const visibleTitleDiv = el.querySelector('.ef__option-title') || el;
      if (visibleTitleDiv) {
        visibleTitleDiv.innerHTML = cleanText + ' ';
        const tooltipContainer = createTooltipContainer(infoText);
        visibleTitleDiv.appendChild(tooltipContainer);
      }

      // Finally, remove the raw token from the attribute itself
      el.setAttribute(attributeName, cleanText);
    } else {
      // Look inside innerHTML & strip out one [[info:…]] token
      const rawHTML = el.innerHTML;
      if (!rawHTML.includes('[[info:')) return;

      const match = rawHTML.match(/\[\[info:(.*?)\]\]/);
      if (!match) return;
      const infoText = match[1].trim();
      const cleanHTML = rawHTML.replace(/\[\[info:(.*?)\]\]/, '').trim();

      el.innerHTML = cleanHTML + ' ';
      const tooltipContainer = createTooltipContainer(infoText);
      el.appendChild(tooltipContainer);
    }
  }

  /**
   * 4) Main init routine: find every .ef__product-option and:
   *    a) Look for data-option-title="…[[info:…]]…" → strip & inject tooltip
   *    b) Look for any visible .ef__option-title innerHTML having [[info:…]] 
   *    c) Same for .ef__option-description, .ef__option-value-description
   *    d) Also handle data-value-title on each <input> for value tooltips
   */
  function initEasyFlowTooltips() {
    document.querySelectorAll('.ef__product-option').forEach((optWrapper) => {
      // 4a) data-option-title (attribute-based)
      replaceInfoTokensInElement(optWrapper, true, 'data-option-title');

      // 4b) visible title DIV
      const titleDiv = optWrapper.querySelector('.ef__option-title');
      if (titleDiv) {
        replaceInfoTokensInElement(titleDiv, false);
      }

      // 4c) description under the title
      const descDiv = optWrapper.querySelector('.ef__option-description');
      if (descDiv) {
        replaceInfoTokensInElement(descDiv, false);
      }

      // 4c-continued) any <em class="ef__option-value-description">
      optWrapper
        .querySelectorAll('.ef__option-value-description')
        .forEach((vd) => {
          replaceInfoTokensInElement(vd, false);
        });

      // 4d) If each <input> has a data-value-title with [[info:…]], update its <label>
      optWrapper
        .querySelectorAll('.ef__product-option-input')
        .forEach((inputEl) => {
          const attrValue = inputEl.getAttribute('data-value-title');
          if (attrValue && attrValue.includes('[[info:')) {
            // Trick: put the raw token into a temp <span>, process it, then copy innerHTML
            const tempSpan = document.createElement('span');
            tempSpan.innerHTML = attrValue; // treat as HTML so we catch tokens
            replaceInfoTokensInElement(tempSpan, false);

            const label = optWrapper.querySelector(`label[for="${inputEl.id}"]`);
            if (label) {
              label.innerHTML = tempSpan.innerHTML;
            }
            // Update the data-value-title to the stripped text
            inputEl.setAttribute(
              'data-value-title',
              tempSpan.textContent.trim() || ''
            );
          }
        });
    });

    // 5) Once done, unhide the EasyFlow root and allow everything to be seen:
    document.querySelectorAll('.ef__product-option-root').forEach((root) => {
      root.classList.add('ef-tooltips-ready');
    });
  }

  /**
   * 6) Wait until DOMContentLoaded, then run init (with a tiny delay).
   *    That way, if EasyFlow is still injecting HTML, we let it finish.
   */
  document.addEventListener('DOMContentLoaded', () => {
    // Give it ~50 ms for EasyFlow to finish rendering, if needed.
    setTimeout(initEasyFlowTooltips, 50);
  });
})();
